import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

/**
 * Makes the columns of every PrimeNG table resizable: a drag handle is added to the right edge of each
 * header cell (double-click it to reset that column), and the widths are saved to a cookie.
 *
 * It matches every <p-table> (no attribute needed) and works on the rendered DOM, so it doesn't matter how
 * a table builds its headers. Widths are keyed by the header's text, and stored per "table location"
 * (the nearest <app-*> component tags around the table, e.g. the project table on the home page is stored
 * separately from the same table on the projects page). Tables at the same location (the per-group tables
 * of a grouped view) share widths and update together.
 */
@Directive({ selector: 'p-table' })
export class ResizableColumnsDirective implements AfterViewInit, OnDestroy {

  private static readonly MIN_WIDTH = 50;
  private static readonly COOKIE_PREFIX = 'colw_';
  // Widths and live instances per location, so tables at the same location stay in sync.
  private static widthsByLocation = new Map<string, { [header: string]: number }>();
  private static instances = new Set<ResizableColumnsDirective>();

  private observer: MutationObserver | null = null;
  private location = '';
  private widths: { [header: string]: number } = {};
  private scheduled = false;

  constructor(private el: ElementRef, private cookieService: CookieService, private zone: NgZone) {}

  ngAfterViewInit() {
    const host = this.el.nativeElement as HTMLElement;
    this.location = this.BuildLocation(host);

    let shared = ResizableColumnsDirective.widthsByLocation.get(this.location);
    if (!shared) {
      shared = this.RestoreFromCookie();
      ResizableColumnsDirective.widthsByLocation.set(this.location, shared);
    }
    this.widths = shared;
    ResizableColumnsDirective.instances.add(this);

    // Headers get re-rendered (sorting, hiding columns, paging...), so decorate again whenever the table's DOM changes.
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.Schedule());
      this.observer.observe(host, { childList: true, subtree: true });
    });
    this.Schedule();
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    ResizableColumnsDirective.instances.delete(this);
  }

  private BuildLocation(host: HTMLElement): string {
    const tags: string[] = [];
    let node: HTMLElement | null = host.parentElement;
    while (node && tags.length < 2) {
      const tag = node.tagName.toLowerCase();
      if (tag.startsWith('app-')) tags.push(tag);
      node = node.parentElement;
    }
    return tags.length ? tags.join('_') : 'page';
  }

  private get cookieName(): string {
    return ResizableColumnsDirective.COOKIE_PREFIX + this.location;
  }

  private RestoreFromCookie(): { [header: string]: number } {
    const restored: { [header: string]: number } = {};
    try {
      const raw = this.cookieService.get(this.cookieName);
      if (!raw) return restored;
      const saved = JSON.parse(raw);
      if (saved && typeof saved === 'object') {
        for (const key of Object.keys(saved)) {
          if (typeof saved[key] === 'number' && saved[key] >= ResizableColumnsDirective.MIN_WIDTH) restored[key] = saved[key];
        }
      }
    } catch (e) {
      console.error('Error restoring column widths from cookie:', e);
    }
    return restored;
  }

  private Save() {
    try {
      this.cookieService.set(this.cookieName, JSON.stringify(this.widths), 365, '/');
    } catch (e) {
      console.error('Error saving column widths to cookie:', e);
    }
  }

  private Schedule() {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(() => {
      this.scheduled = false;
      this.Decorate();
    });
  }

  private Decorate() {
    const host = this.el.nativeElement as HTMLElement;
    const headers = host.querySelectorAll<HTMLElement>('thead > tr:first-child > th');
    headers.forEach(th => {
      // Row-expansion content can hold its own nested table, which has its own directive instance.
      if (th.closest('p-table') !== host) return;

      // The key is read once, while the header is at rest - an open column-filter menu lives inside the th and
      // would otherwise change its text.
      if (th.dataset['colKey'] === undefined) {
        th.dataset['colKey'] = (th.textContent || '').replace(/\s+/g, ' ').trim();
      }
      const key = th.dataset['colKey'];
      if (!key) return; // checkbox / expander / action columns have no title - leave them alone

      this.ApplyWidth(th, this.widths[key]);

      if (!th.querySelector(':scope > .col-resizer')) {
        const handle = document.createElement('span');
        handle.className = 'col-resizer';
        handle.title = 'Drag to resize, double-click to reset';
        handle.addEventListener('mousedown', (e: MouseEvent) => this.StartResize(e, th, key));
        // The header is usually also the sort trigger - don't sort when releasing the handle
        handle.addEventListener('click', (e: MouseEvent) => e.stopPropagation());
        handle.addEventListener('dblclick', (e: MouseEvent) => { e.stopPropagation(); this.Reset(key); });
        th.appendChild(handle);
      }
    });

    // Once columns have explicit widths the table must stop stretching to fill its container - otherwise
    // any width freed by shrinking a column is handed out to the other columns, making them grow.
    const table = host.querySelector('table');
    if (table && table.closest('p-table') === host) {
      table.style.width = Object.keys(this.widths).length > 0 ? 'auto' : '';
    }
  }

  // `width` is the visible (border-box) width. The th style width means the content box when box-sizing is
  // content-box, so take the padding and border back off in that case.
  private ApplyWidth(th: HTMLElement, width: number | undefined) {
    if (width) {
      const style = getComputedStyle(th);
      const extra = style.boxSizing === 'content-box'
        ? (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0) + (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0)
        : 0;
      th.style.width = th.style.minWidth = th.style.maxWidth = Math.max(0, width - extra) + 'px';
    } else {
      th.style.width = th.style.minWidth = th.style.maxWidth = '';
    }
    th.style.position = 'relative';
  }

  private StartResize(event: MouseEvent, th: HTMLElement, key: string) {
    event.preventDefault();
    event.stopPropagation();
    // Lock every titled column at its current width first, so only the dragged one changes
    this.FreezeColumns();
    const start_x = event.clientX;
    const start_width = th.getBoundingClientRect().width;

    const onMove = (e: MouseEvent) => {
      this.widths[key] = Math.max(ResizableColumnsDirective.MIN_WIDTH, Math.round(start_width + (e.clientX - start_x)));
      this.SyncSameLocation();
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      this.Save();
      // Releasing over the header would otherwise register as a click on it and sort the column
      const swallow = (e: Event) => { e.stopPropagation(); e.preventDefault(); };
      window.addEventListener('click', swallow, true);
      setTimeout(() => window.removeEventListener('click', swallow, true), 0);
    };
    document.body.style.userSelect = 'none';
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });
  }

  private FreezeColumns() {
    const host = this.el.nativeElement as HTMLElement;
    host.querySelectorAll<HTMLElement>('thead > tr:first-child > th').forEach(th => {
      const key = th.dataset['colKey'];
      if (!key || th.closest('p-table') !== host) return;
      if (this.widths[key] === undefined) this.widths[key] = Math.round(th.getBoundingClientRect().width);
    });
    this.SyncSameLocation();
  }

  private Reset(key: string) {
    delete this.widths[key];
    this.SyncSameLocation();
    this.Save();
  }

  private SyncSameLocation() {
    ResizableColumnsDirective.instances.forEach(instance => {
      if (instance.location === this.location) instance.Decorate();
    });
  }
}
